import type { VersionResponse } from "@/types/common/version";

import { changeLocale } from "@formkit/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { setLocale as setVeeValidateLocale } from "@vee-validate/i18n";
import { useIntervalFn, useLocalStorage } from "@vueuse/core";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { computed, onMounted, onScopeDispose, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import {
  AUTO_UPDATE_HOUR,
  VERSION_LOCAL_STORAGE_KEY,
  VERSION_POLLING_INTERVAL_MS,
} from "@/constants/version.constants";
import { primevueEnLocale } from "@/lib/primevue/locales/primevue-en-locale.ts";
import { primeVueFrLocale } from "@/lib/primevue/locales/primevue-fr-locale.ts";
import { createSingleErrorLogger } from "@/lib/validation-utils.ts";
import { ELocale } from "@/stores/interface/interface.types";
import { versionResponseSchema } from "@/types/common/version";

import { usePrimeVue } from "primevue/config";

export const INTERFACE_STORE_KEY = "INTERFACE_STORE";

export type TLocale = `${ELocale}`;

const PRIMEVUE_LOCALES = {
  [ELocale.EN]: primevueEnLocale,
  [ELocale.FR]: primeVueFrLocale,
};
const ALLOWED_LANGUAGES = [ELocale.EN.valueOf(), ELocale.FR.valueOf()];

const getNavigatorLanguage = (): TLocale => {
  if (!navigator.languages?.length) {
    return "fr";
  }

  const lang = navigator.languages[0].split("-")[0];
  if (!ALLOWED_LANGUAGES.includes(lang)) {
    return "fr";
  }

  return lang as TLocale;
};

const logVersionValidationError = createSingleErrorLogger();

/**
 * Calculate milliseconds until the next occurrence of the auto-update hour.
 */
function calculateMsUntilAutoUpdate(): number {
  const now = dayjs();
  const target = dayjs(now).hour(AUTO_UPDATE_HOUR).minute(0).second(0).millisecond(0).add(1, "day");

  return target.diff(now);
}

export const useInterfaceStore = defineStore(INTERFACE_STORE_KEY, () => {
  const i18n = useI18n();
  const primevue = usePrimeVue();
  const queryClient = useQueryClient();

  const leftPanelLocalStorage = useLocalStorage("@ui/left-panel-expanded", "true");
  const largeScreen = useLocalStorage("@ui/large-screen", "false");
  const fontSize = useLocalStorage("@ui/font-size", 14);

  const isSiteReinterventionSidebarExpanded = ref(false);
  const locale = useLocalStorage<TLocale>("@ui/locale", getNavigatorLanguage());
  const settingsModalVisible = ref(false);

  // Version check state
  const storedBuild = useLocalStorage<string | null>(VERSION_LOCAL_STORAGE_KEY, null);
  const currentBuild = ref<string | null>(null);
  const isUpdateAvailable = ref(false);
  const isUpdateDismissed = ref(false);
  const autoUpdateTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

  onMounted(() => {
    updateLocale(locale.value);
    void initVersionCheck();
  });

  onScopeDispose(() => {
    if (autoUpdateTimeoutId.value) {
      clearTimeout(autoUpdateTimeoutId.value);
    }
  });

  const isLeftPanelExpanded = computed(() => {
    return leftPanelLocalStorage.value === "true";
  });

  const isLargeScreen = computed(() => {
    return largeScreen.value === "true";
  });

  const currentLocale = computed(() => {
    return locale.value;
  });

  function updateLocale(nextLocale: TLocale) {
    i18n.locale.value = nextLocale;
    changeLocale(nextLocale);
    setVeeValidateLocale(nextLocale);
    primevue.config.locale = PRIMEVUE_LOCALES[nextLocale];

    locale.value = nextLocale;
  }

  function setLargeScreen(large: boolean) {
    largeScreen.value = large ? "true" : "false";
  }

  function toggleLeftPanelExpanded() {
    leftPanelLocalStorage.value = leftPanelLocalStorage.value === "true" ? "false" : "true";
  }

  function toggleSiteReinterventionSidebarExpanded(next?: boolean) {
    if (next === undefined) {
      isSiteReinterventionSidebarExpanded.value = !isSiteReinterventionSidebarExpanded.value;
    } else {
      isSiteReinterventionSidebarExpanded.value = next;
    }
  }

  /**
   * Fetch version.json with cache-busting and validate with Zod.
   */
  async function fetchVersion(): Promise<VersionResponse | null> {
    try {
      const url = `/version.json?t=${Date.now()}`;
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      const result = versionResponseSchema.safeParse(data);
      if (!result.success) {
        logVersionValidationError(new Error("Version response validation failed"), {
          validationError: result.error.format(),
          receivedData: data,
        });
        return null;
      }
      return result.data;
    } catch {
      return null;
    }
  }

  /**
   * Initialize version check: fetch initial version and start polling.
   */
  async function initVersionCheck() {
    const version = await fetchVersion();
    if (version) {
      currentBuild.value = version.build;
      // Update stored build on app load/reload
      storedBuild.value = version.build;
    }

    // Start polling
    useIntervalFn(async () => {
      const version = await fetchVersion();
      if (version) {
        // Check if build changed compared to stored value
        if (storedBuild.value && version.build !== storedBuild.value) {
          isUpdateAvailable.value = true;
          scheduleAutoUpdate();
        }
        currentBuild.value = version.build;
      }
    }, VERSION_POLLING_INTERVAL_MS);
  }

  /**
   * Schedule automatic update at the configured hour.
   */
  function scheduleAutoUpdate() {
    if (autoUpdateTimeoutId.value) {
      clearTimeout(autoUpdateTimeoutId.value);
    }
    const delay = calculateMsUntilAutoUpdate();
    autoUpdateTimeoutId.value = setTimeout(() => {
      window.location.reload();
    }, delay);
  }

  /**
   * Cancel scheduled auto-update.
   */
  function cancelAutoUpdate() {
    if (autoUpdateTimeoutId.value) {
      clearTimeout(autoUpdateTimeoutId.value);
      autoUpdateTimeoutId.value = null;
    }
  }

  /**
   * Dismiss update popup and schedule auto-update.
   */
  function dismissUpdate() {
    isUpdateDismissed.value = true;
    scheduleAutoUpdate();
  }

  /**
   * Refresh the application immediately.
   */
  function refreshNow() {
    cancelAutoUpdate();
    window.location.reload();
  }

  /**
   * invalidate local-dependant queries - i.e. endpoints that return backend translated values
   * see {@link @/components/phoning/close-call-form.vue}
   */
  watch(currentLocale, () => {
    queryClient.invalidateQueries({
      queryKey: [{ localeDependant: true }],
      exact: false,
      type: "active",
    });
  });

  watch(
    () => fontSize.value,
    () => {
      document.documentElement.style.setProperty("--html-font-size", `${fontSize.value}px`);
    },
    { immediate: true },
  );

  return {
    isLeftPanelExpanded,
    isSiteReinterventionSidebarExpanded,
    settingsModalVisible,
    currentLocale,
    isLargeScreen,
    fontSize,
    // Version check
    currentBuild,
    storedBuild,
    isUpdateAvailable,
    isUpdateDismissed,
    dismissUpdate,
    refreshNow,
    setLargeScreen,
    toggleLeftPanelExpanded,
    toggleSiteReinterventionSidebarExpanded,
    updateLocale,
  };
});
